// The same port (or airport) under different codes. A carrier may write its own code for a port (CMA CGM:
// Nanjing CNNKG, Incheon KRICH) where Monada, a rate platform or another carrier uses another
// (CNNJG, KRINC). Each rate keeps the code it was loaded with; a search for any code in a group
// finds the rates of all of them. Used by the server (rate search, Elariom, publish checks) and the
// browser (Rates page).
//
// The list is explicit and checked by hand: two ports with the same name are not always the same
// (Jiujiang, Guangdong CNJUJ/CNJJG is not Jiujiang, Jiangxi CNJIU). Add a group only when the two
// codes are known to be one port. Started 2026-10-08 from the CMA CGM / CargoFive comparison
// (docs/air-agent/cases/06-cma-logwin-sullivan.md).

export const PORT_GROUPS = [
    ['CNNKG', 'CNNJG'], // Nanjing
    ['KRICH', 'KRINC'], // Incheon
    ['THSGZ', 'THSGK'], // Songkhla
    ['CNBHY', 'CNBIH'], // Beihai
    ['CNHAK', 'CNHKO'], // Haikou
    ['CNQZJ', 'CNQZL'], // Quanzhou
    ['CNSWA', 'CNSTG'], // Shantou
    ['CNTAG', 'CNTAC'], // Taicang
    ['CNZAP', 'CNZPU'], // Zhapu
    ['CNCKG', 'CNCQI'], // Chongqing
    ['CNCSX', 'CNCSH'], // Changsha
    ['CNCGU', 'CNCGS'], // Changshu
    ['CNFOC', 'CNFZH'], // Fuzhou (Mawei)
    ['CNLZU', 'CNLUZ'], // Luzhou
    ['CNYIC', 'CNYCG'], // Yichang
    ['CNJGZ', 'CNJZK'], // Jingzhou
    ['CNJJG', 'CNJUJ'], // Jiujiang, Guangdong (not CNJIU, Jiujiang in Jiangxi)
    ['CNQYN', 'CNQGY'], // Qingyuan
    ['CNSHG', 'CNNHS'], // Sanshan, Foshan
    ['CNYUY', 'CNYYA'], // Yueyang
    ['IDKTJ', 'IDTJB'], // Kuala Tanjung
];

// Airports: an IATA metropolitan (city) code and the airports it covers, the city code first. A
// carrier or agent may quote to the city (LON) or to one airport (LHR); a search for any of them
// finds the rates of all. Started 2026-10-08 with the common IATA city codes.
export const AIRPORT_GROUPS = [
    ['NYC', 'JFK', 'LGA', 'EWR'], // New York
    ['LON', 'LHR', 'LGW', 'STN', 'LTN', 'LCY', 'SEN'], // London
    ['TYO', 'HND', 'NRT'], // Tokyo
    ['PAR', 'CDG', 'ORY', 'BVA'], // Paris
    ['WAS', 'IAD', 'DCA', 'BWI'], // Washington
    ['CHI', 'ORD', 'MDW'], // Chicago
    ['BJS', 'PEK', 'PKX'], // Beijing
    ['MIL', 'MXP', 'LIN', 'BGY'], // Milan
    ['OSA', 'KIX', 'ITM', 'UKB'], // Osaka
    ['SEL', 'ICN', 'GMP'], // Seoul
    ['SHA', 'PVG'], // Shanghai (SHA is also Hongqiao airport)
    ['ROM', 'FCO', 'CIA'], // Rome
    ['STO', 'ARN', 'BMA', 'NYO', 'VST'], // Stockholm
    ['MOW', 'SVO', 'DME', 'VKO', 'ZIA'], // Moscow
    ['SAO', 'GRU', 'CGH', 'VCP'], // São Paulo
    ['RIO', 'GIG', 'SDU'], // Rio de Janeiro
    ['BUE', 'EZE', 'AEP'], // Buenos Aires
    ['YTO', 'YYZ', 'YTZ', 'YKF'], // Toronto
    ['YMQ', 'YUL', 'YMX'], // Montreal
    ['DTT', 'DTW', 'DET'], // Detroit
    ['QDF', 'DFW', 'DAL'], // Dallas
    ['QHO', 'IAH', 'HOU'], // Houston
    ['ORL', 'MCO'], // Orlando (ORL is also Executive airport)
    ['JKT', 'CGK', 'HLP'], // Jakarta
    ['BKK', 'DMK'], // Bangkok (BKK is also Suvarnabhumi)
    ['IST', 'SAW'], // Istanbul (IST is also Istanbul Airport)
    ['TPE', 'TSA'], // Taipei
    ['KUL', 'SZB'], // Kuala Lumpur
    ['DXB', 'DWC'], // Dubai
    ['NGO', 'NKM'], // Nagoya
    ['SPK', 'CTS', 'OKD'], // Sapporo
    ['REK', 'KEF', 'RKV'], // Reykjavik
    ['BHZ', 'CNF', 'PLU'], // Belo Horizonte
    ['OSL', 'TRF', 'RYG'], // Oslo
    ['MEX', 'NLU'], // Mexico City
    ['TCI', 'TFN', 'TFS'], // Tenerife
    ['EAP', 'BSL', 'MLH'], // Basel-Mulhouse
    ['GLA', 'PIK'], // Glasgow
    ['IEV', 'KBP'], // Kyiv
    ['BUH', 'OTP', 'BBU'], // Bucharest
    ['CTU', 'TFU'], // Chengdu
];

const BY_CODE = new Map();
[...PORT_GROUPS, ...AIRPORT_GROUPS].forEach(group => group.forEach(code => BY_CODE.set(code.toUpperCase(), group)));

// Every code of the same port, the given one first; a code in no group is alone.
export function samePortCodes(code) {
    const c = String(code || '').toUpperCase();
    const group = BY_CODE.get(c);
    return group ? [c, ...group.filter(x => x !== c)] : [c];
}

// Whether two place ids are the same port.
export function isSamePort(a, b) {
    if (!a || !b) return false;
    const x = String(a).toUpperCase();
    const y = String(b).toUpperCase();
    return x === y || (BY_CODE.get(x) && BY_CODE.get(x) === BY_CODE.get(y));
}

// The airports of an IATA city code (LON → LHR, LGW, …); empty when the code is not a city code.
export function cityAirports(code) {
    const c = String(code || '').toUpperCase();
    const group = AIRPORT_GROUPS.find(g => g[0] === c);
    return group ? group.slice(1) : [];
}

// One key for a port whatever its code (the group's first code), for grouping and comparing.
export function portKey(code) {
    const c = String(code || '').toUpperCase();
    return (BY_CODE.get(c) || [c])[0];
}
